import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import {
  Box,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { StoreAssetForm } from '@/components/forms/StoreAssetForm'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { emptyStoreAssetForm, type StoreAssetFormData } from '@/lib/validations/asset'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  createStoreAsset,
  emptyStoreAssetOptionLists,
  fetchBrandOptions,
  fetchStoreAssetOptionLists,
  fetchStoreDetails,
  importStoreAssetAssignments,
  mapAssetFormToPayload,
  mapStoreAssetToFormData,
  updateStoreAsset,
  type BrandOption,
  type StoreAssetOptionLists,
  type StoreAssetRecord,
} from '@/lib/services/storeService'

type AssetTabValue = 'all' | 'trash'

function resolveStoreAssetId(asset: StoreAssetRecord): number {
  return asset.asset_id ?? asset.id
}

export default function StoreDetailsPage() {
  const colors = useThemeColors()
  const { id } = useParams<{ id: string }>()
  const storeId = Number(id)
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const access = useAccessCheck()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const [loading, setLoading] = useState(true)
  const [store, setStore] = useState<any>(null)
  const [summary, setSummary] = useState<Record<string, any>>({})
  const [summaryCounts, setSummaryCounts] = useState({ all: 0, audited: 0, notAudited: 0 })
  const [activeTab, setActiveTab] = useState<AssetTabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [assetFilter, setAssetFilter] = useState<'all' | 'audited' | 'not_audited'>('all')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [brands, setBrands] = useState<BrandOption[]>([])
  const [assetOptionLists, setAssetOptionLists] = useState<StoreAssetOptionLists>(
    emptyStoreAssetOptionLists()
  )
  const [optionsReady, setOptionsReady] = useState(false)
  const [assetFormDefaults, setAssetFormDefaults] = useState<StoreAssetFormData>(emptyStoreAssetForm())
  const [editingAsset, setEditingAsset] = useState<StoreAssetRecord | null>(null)
  const [savingAsset, setSavingAsset] = useState(false)
  const [formKey, setFormKey] = useState('new')
  const [assetsList, setAssetsList] = useState<StoreAssetRecord[]>([])

  const loadOptionLists = useCallback(async () => {
    const [brandRows, optionRows] = await Promise.allSettled([
      fetchBrandOptions(),
      fetchStoreAssetOptionLists(),
    ])
    setBrands(brandRows.status === 'fulfilled' ? brandRows.value : [])
    setAssetOptionLists(optionRows.status === 'fulfilled' ? optionRows.value : emptyStoreAssetOptionLists())
    setOptionsReady(true)
  }, [])

  const reloadSummary = useCallback(async () => {
    if (!storeId) return
    const details = await fetchStoreDetails(storeId)
    const list = Array.isArray(details.assets_list) ? details.assets_list : []
    setSummary(details.assets_summary ?? {})
    setSummaryCounts({
      all: list.length,
      audited: list.filter((a) => a.audit_status === 'audited').length,
      notAudited: list.filter((a) => a.audit_status !== 'audited').length,
    })
    setAssetsList(list)
  }, [storeId])

  const loadData = async () => {
    if (!storeId) return
    setLoading(true)
    try {
      const details = await fetchStoreDetails(storeId)
      setStore(details.store)
      await reloadSummary()
      void loadOptionLists()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    setActiveTab('all')
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId])

  useEffect(() => {
    if (!loading && storeId) {
      void reloadSummary()
    }
  }, [totalRows, totalTrash, loading, storeId, reloadSummary])

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    setSearchQuery(searchRef.current?.value ?? '')
    tableRef.current?.clearPage()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch()
    }
  }

  const handleCreate = () => {
    if (!optionsReady) {
      showError('Option lists are still loading. Please try again in a moment.')
      return
    }
    setEditingAsset(null)
    setAssetFormDefaults(emptyStoreAssetForm())
    setFormKey(`new-${Date.now()}`)
    onOpen()
  }

  const handleEdit = useCallback((asset: StoreAssetRecord) => {
    setEditingAsset(asset)
    setAssetFormDefaults(mapStoreAssetToFormData(asset))
    setFormKey(`edit-${resolveStoreAssetId(asset)}`)
    onOpen()
  }, [onOpen])

  const handleOpenAssetModal = useCallback(
    (assetOrRow: number | string | StoreAssetRecord) => {
      const asset =
        typeof assetOrRow === 'object'
          ? assetOrRow
          : assetsList.find((a) => resolveStoreAssetId(a) === assetOrRow || a.id === assetOrRow)
      if (asset) {
        handleEdit(asset)
      } else {
        showError('Asset not found')
      }
    },
    [assetsList, handleEdit, showError]
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        audit_status: {
          name: 'Status',
          withSort: false,
          badge: {
            audited: 'green',
            not_audited: 'yellow',
          },
          badgeLabels: {
            audited: 'Audited',
            not_audited: 'Un-audited',
          },
        },
        category_name: { name: 'Category', withSort: true, maxWidth: '120px' },
        brand_name: { name: 'Brand', withSort: true, maxWidth: '120px' },
        model: { name: 'Model', withSort: true, maxWidth: '120px' },
        tag_number: { name: 'Tag', withSort: true, maxWidth: '120px' },
        serial_number: { name: 'Serial', withSort: true, maxWidth: '160px' },
        function: { name: 'Function', withSort: true, maxWidth: '160px' },
      },
      dataSource:
        activeTab === 'trash'
          ? `/store-management/archived/stores/${storeId}/assets`
          : `/store-management/stores/${storeId}/assets`,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: activeTab === 'trash' ? 'No trashed assets for this store' : 'No assets assigned to this store',
    }),
    [activeTab, storeId]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      ...(assetFilter !== 'all' ? { audit_status: assetFilter } : {}),
    }),
    [searchQuery, assetFilter]
  )

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const getActionMessage = useCallback((action: string) => {
    if (action === 'Trash') {
      return 'Remove this asset from the store? It can be restored from Trash.'
    }
    if (action === 'Delete' || action === 'BulkDelete') {
      return 'Permanently delete this asset and remove it from the store? This cannot be undone.'
    }
    return undefined
  }, [])

  const handleCloseDrawer = () => {
    onClose()
    setEditingAsset(null)
  }

  const refreshAssetsTable = async () => {
    tableRef.current?.reload()
    await reloadSummary()
  }

  const handleSave = async (data: StoreAssetFormData) => {
    if (!storeId) return
    setSavingAsset(true)
    try {
      const payload = mapAssetFormToPayload(data)
      if (editingAsset) {
        await updateStoreAsset(storeId, resolveStoreAssetId(editingAsset), payload)
        showSuccess('Success', 'Asset updated successfully')
      } else {
        await createStoreAsset(storeId, payload)
        showSuccess('Success', 'Asset created and assigned successfully')
      }
      handleCloseDrawer()
      await refreshAssetsTable()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setSavingAsset(false)
    }
  }

  const handleBatchUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const result = await importStoreAssetAssignments(storeId, file)
      showSuccess('Success', `Imported ${result?.imported ?? 0} row(s)`)
      await refreshAssetsTable()
    } catch (error: unknown) {
      showError(error)
    } finally {
      event.target.value = ''
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" minH={0} overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={4} align="stretch" flex="1" minH={0} overflowY="auto" overflowX="hidden" pb={4}>
          <Card>
            <CardHeader py={3}>
              <HStack justify="space-between">
                <Heading size="md">Store Details</Heading>
                <Button size="sm" onClick={() => navigate('/organization/stores')}>Back to Stores</Button>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              {loading ? (
                <Text>Loading...</Text>
              ) : (
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }} gap={4}>
                  <GridItem><Text fontWeight="bold">Branch Name</Text><Text>{store?.branch_name ?? '-'}</Text></GridItem>
                  <GridItem><Text fontWeight="bold">Branch Code</Text><Text>{store?.branch_code ?? '-'}</Text></GridItem>
                  <GridItem><Text fontWeight="bold">Business Unit</Text><Text>{store?.business_unit_name ?? '-'}</Text></GridItem>
                  <GridItem><Text fontWeight="bold">Address</Text><Text>{store?.branch_address ?? '-'}</Text></GridItem>
                  <GridItem><Text fontWeight="bold">Email</Text><Text>{store?.branch_email ?? '-'}</Text></GridItem>
                  <GridItem><Text fontWeight="bold">Contact</Text><Text>{store?.contact_person ?? '-'} {store?.contact_number ? `(${store.contact_number})` : ''}</Text></GridItem>
                </Grid>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader py={3}><Heading size="sm">Assets Summary by Category</Heading></CardHeader>
            <CardBody pt={0}>
              <Grid templateColumns={{ base: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }} gap={3}>
                <Stat><StatLabel>All Assets</StatLabel><StatNumber>{summaryCounts.all}</StatNumber></Stat>
                <Stat><StatLabel>Audited</StatLabel><StatNumber>{summaryCounts.audited}</StatNumber></Stat>
                <Stat><StatLabel>Un-audited</StatLabel><StatNumber>{summaryCounts.notAudited}</StatNumber></Stat>
                <Stat><StatLabel>Categories</StatLabel><StatNumber>{Object.keys(summary).length}</StatNumber></Stat>
              </Grid>
              {Object.keys(summary).length > 0 && (
                <Box mt={4} overflowX="auto">
                  <Table size="sm">
                    <Thead><Tr><Th>Category</Th><Th>Total</Th><Th>Audited</Th><Th>Un-audited</Th><Th>Active</Th></Tr></Thead>
                    <Tbody>
                      {Object.entries(summary).map(([name, row]) => (
                        <Tr key={name}>
                          <Td>{name}</Td><Td>{row.count}</Td><Td>{row.audited_count}</Td><Td>{row.pending_count}</Td><Td>{row.active_count}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>

          <Card flex="1" minH="420px" display="flex" flexDirection="column" overflow="hidden">
            <CardHeader py={3} flexShrink={0}>
              <HStack justify="space-between" align="flex-end" flexWrap="wrap" gap={2}>
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                  <TabList borderBottom="none">
                    <Tab>
                      All{' '}
                      <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash{' '}
                      <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <HStack flexWrap="wrap" spacing={2}>
                  <Button size="sm" colorScheme="blue" onClick={handleCreate}>Add Asset</Button>
                  <Button as="label" size="sm" variant="outline">
                    Batch Upload
                    <Input type="file" accept=".csv" display="none" onChange={handleBatchUpload} />
                  </Button>
                </HStack>
              </HStack>
            </CardHeader>

            <CardHeader py={1} pt={0} pb={2} flexShrink={0}>
              <HStack spacing={3} flexWrap="wrap">
                <Box flex="0 1 auto" minW="220px" maxW="360px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search tag, serial, brand, model..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search assets"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
                {activeTab === 'all' && (
                  <Select
                    size="sm"
                    value={assetFilter}
                    onChange={(e) => {
                      setAssetFilter(e.target.value as 'all' | 'audited' | 'not_audited')
                      tableRef.current?.clearPage()
                    }}
                    w="180px"
                  >
                    <option value="all">All audit status</option>
                    <option value="audited">Audited</option>
                    <option value="not_audited">Un-audited</option>
                  </Select>
                )}
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} px={4} flex="1" minH={0} overflow="hidden" display="flex" flexDirection="column">
              <Box flex="1" minH={0} borderWidth="1px" borderColor={colors.border} borderRadius="md" overflow="hidden">
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  getActionMessage={getActionMessage}
                  onOpenModal={handleOpenAssetModal}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Drawer isOpen={isOpen} onClose={handleCloseDrawer} placement="right" size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {editingAsset ? 'Edit Asset' : 'Add Asset'}
          </DrawerHeader>
          <DrawerBody py={4}>
            <StoreAssetForm
              key={formKey}
              mode={editingAsset ? 'edit' : 'create'}
              defaultValues={assetFormDefaults}
              audited={editingAsset?.audit_status === 'audited'}
              brandOptions={brands}
              optionLists={assetOptionLists}
              onSubmit={handleSave}
              onCancel={handleCloseDrawer}
              isSubmitting={savingAsset}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </AdminLayout>
  )
}
